import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getActivityLogs, getDepartments, getUsers } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Search, Activity, Monitor, Smartphone, Globe, Calendar,
  User, ChevronLeft, ChevronRight, RefreshCw, Play, Eye, 
  BookOpen, MessageSquare, Camera, Mouse, Clock, Keyboard
} from 'lucide-react';

const ACTION_TYPES = [
  { value: 'all', label: 'All Actions' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'view_call', label: 'View Call' },
  { value: 'play_call', label: 'Play Call Audio' },
  { value: 'play_own_call', label: 'Play Own Call' },
  { value: 'play_other_call', label: 'Play Other\'s Call' },
  { value: 'view_dashboard', label: 'View Dashboard' },
  { value: 'view_coaching', label: 'View Coaching' },
  { value: 'view_homework', label: 'View Homework' },
  { value: 'complete_homework', label: 'Complete Homework' },
  { value: 'view_ai_feedback', label: 'View AI Feedback' },
  { value: 'view_library', label: 'View Library' },
  { value: 'search_library', label: 'Search Library' },
  { value: 'view_kpi', label: 'View KPI' },
  { value: 'view_reports', label: 'View Reports' },
  { value: 'screenshot_attempt', label: 'Screenshot Attempt' },
  { value: 'download', label: 'Download' },
  { value: 'session_active', label: 'Session Active' },
  { value: 'session_idle', label: 'Session Idle' },
];

const getActionBadgeVariant = (action) => {
  switch (action?.toLowerCase()) {
    case 'login': return 'default';
    case 'logout': return 'secondary';
    case 'view_call': 
    case 'play_call':
    case 'play_own_call':
      return 'outline';
    case 'play_other_call':
      return 'default';
    case 'complete_homework':
      return 'default';
    case 'screenshot_attempt':
      return 'destructive';
    case 'session_idle':
      return 'secondary';
    default: return 'outline';
  }
};

const getActionIcon = (action) => {
  switch (action?.toLowerCase()) {
    case 'login':
    case 'logout':
      return <User className="h-3 w-3" />;
    case 'play_call':
    case 'play_own_call':
    case 'play_other_call':
      return <Play className="h-3 w-3" />;
    case 'view_call':
    case 'view_dashboard':
    case 'view_coaching':
    case 'view_homework':
    case 'view_library':
    case 'view_kpi':
    case 'view_reports':
      return <Eye className="h-3 w-3" />;
    case 'complete_homework':
      return <BookOpen className="h-3 w-3" />;
    case 'view_ai_feedback':
      return <MessageSquare className="h-3 w-3" />;
    case 'screenshot_attempt':
      return <Camera className="h-3 w-3" />;
    case 'session_active':
      return <Mouse className="h-3 w-3" />;
    case 'session_idle':
      return <Clock className="h-3 w-3" />;
    default:
      return <Activity className="h-3 w-3" />;
  }
};

const getDeviceIcon = (device) => {
  if (device === 'Mobile') return <Smartphone className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
};

export default function AdminActivityLog() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    user_id: 'all',
    department_id: 'all',
    action: 'all',
    date_from: '',
    date_to: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: page * pageSize
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.department_id !== 'all') params.department_id = filters.department_id;
      if (filters.action !== 'all') params.action = filters.action;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const { data } = await getActivityLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load activity logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      getUsers().then(res => setUsers(res.data)),
      getDepartments().then(res => setDepartments(res.data))
    ]);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filters.user_id, filters.department_id, filters.action, filters.date_from, filters.date_to]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6" data-testid="admin-activity-log">
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Log
              </CardTitle>
              <CardDescription>
                Monitor user sessions, logins, and actions across the platform
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => fetchLogs()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user name, IP address..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
              />
            </div>
            
            <Select value={filters.user_id} onValueChange={(v) => { setFilters({...filters, user_id: v}); setPage(0); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.department_id} onValueChange={(v) => { setFilters({...filters, department_id: v}); setPage(0); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filters.action} onValueChange={(v) => { setFilters({...filters, action: v}); setPage(0); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map(action => (
                  <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => { setFilters({...filters, date_from: e.target.value}); setPage(0); }}
                className="w-[150px]"
                placeholder="From"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => { setFilters({...filters, date_to: e.target.value}); setPage(0); }}
                className="w-[150px]"
                placeholder="To"
              />
            </div>
            
            <Button type="submit" variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </form>
          
          {/* Results Summary */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {logs.length} of {total} activity records</span>
            {totalPages > 1 && (
              <span>Page {page + 1} of {totalPages}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
              <p className="text-muted-foreground">No activity logs found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Activity will appear here when users interact with the platform
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Device / Browser</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Date & Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{log.user_name || 'Unknown User'}</p>
                            <p className="text-xs text-muted-foreground">{log.user_id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getActionBadgeVariant(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(log.device)}
                          <div>
                            <p className="text-sm">{log.browser || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{log.os || 'Unknown OS'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono text-sm">{log.ip_address || 'N/A'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm">{new Date(log.created_at).toLocaleDateString()}</p>
                            <p className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 p-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = page < 2 ? i : page > totalPages - 3 ? totalPages - 5 + i : page - 2 + i;
                      if (pageNum < 0 || pageNum >= totalPages) return null;
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum + 1}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
